import { UserProfession } from '../../../enums/UserProfession';

/**
 * GOTU-5263 — reshape the `GET /admin/office/:id` contract (org-service-backed)
 * into the office-profile subset the DNT profile reads off
 * `state.users.currentUser`, in the exact casing/nesting the components expect.
 *
 * This adapter is the single source of truth for WHICH office fields org-service
 * owns, which makes its allowlist a trust boundary: `/admin/users/:id` stays
 * authoritative for the user's phone (`contact_number`), admins, T&C and account
 * flags, so a contract that carries them (a broken/evolving org-service
 * response) must not have them reach `currentUser`. See `mergeOfficeSlice`.
 */

/**
 * Top-level office fields org-service owns, same names as on `currentUser`.
 *
 * `address` is deliberately excluded even though the contract carries it: its
 * entries have no `id`/`_id`, but the admin address card deletes by `address.id`
 * and keys by `address._id`, and the overlay would replace the array wholesale
 * (dropping entries on a count mismatch). Office address therefore stays on the
 * monolith read — kept in sync via Kafka, so the displayed value is unchanged —
 * until org-service exposes stable address ids and the write path is migrated.
 */
const MOVED_TOP_LEVEL = [
  'office_name',
  'web_site',
  'profile_image',
  'average_rating',
  'dsoName',
  'parkingInformation',
];

/**
 * `office_info` subfields org-service owns. Deliberately excludes `insurances`
 * (credential-service), `bestInDental`/`onboardingStartDate` (unread by the
 * admin profile), and the stayed T&C subfields (`gotuTerms`/`psaTerms`).
 */
const MOVED_OFFICE_INFO = [
  'officeType',
  'ramVerified',
  'timePerPatientRDH',
  'timePerPatientDA',
  'timePerPatientDN',
  'isAssistedHygiene',
  'productivity',
];

const isPlainObject = (value) =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

/**
 * The profession codes an office's permanent-staff productivity tracks — the
 * three rows the Account-Info card renders. A deliberate subset of
 * `UserProfession` (office productivity has never carried FD/EFDA counts),
 * sourced from the enum so the codes live in one place rather than being
 * re-hardcoded here.
 */
const PRODUCTIVITY_PROFESSIONS = [UserProfession.RDH, UserProfession.DA, UserProfession.DN];

/**
 * Reshape contract productivity to the DNT card's shape: `permanentStaff` keyed
 * by UPPERCASE profession codes (`UserProfession.RDH/DA/DN`), the casing the
 * Account-Info card reads. Accepts either casing from the wire and always emits
 * uppercase. `null`/`undefined` pass straight through — an absent value must not
 * become an object.
 *
 * @param {object | null | undefined} productivity
 * @returns {object | null | undefined}
 */
export const normalizeProductivity = (productivity) => {
  if (!isPlainObject(productivity)) {
    return productivity;
  }

  const staff = isPlainObject(productivity.permanentStaff) ? productivity.permanentStaff : {};
  const permanentStaff = PRODUCTIVITY_PROFESSIONS.reduce((acc, code) => {
    acc[code] = staff[code] ?? staff[code.toLowerCase()] ?? null;
    return acc;
  }, {});

  return {
    operatories: productivity.operatories ?? null,
    permanentStaff,
    bookedOutPatients: productivity.bookedOutPatients ?? null,
  };
};

/**
 * Turn the office contract into the office slice to overlay onto `currentUser`.
 * Only allowlisted keys are copied, and only when the contract actually carries
 * them — a key present-with-null is kept (a legitimate clear), a key the
 * contract omits is dropped so the merge preserves the monolith value. A
 * malformed body (null / non-object / array) yields an empty slice, so a
 * partial or broken 200 degrades to the monolith read instead of throwing.
 *
 * @param {import('../types/Office.api').IOfficeContractResponse | unknown} contract
 * @returns {object} the office slice (a partial `currentUser` shape)
 */
export const toOfficeSlice = (contract) => {
  if (!isPlainObject(contract)) {
    return {};
  }

  const slice = {};
  MOVED_TOP_LEVEL.forEach((key) => {
    if (key in contract) {
      slice[key] = contract[key];
    }
  });

  if (isPlainObject(contract.office_info)) {
    const source = contract.office_info;
    const officeInfo = {};
    MOVED_OFFICE_INFO.forEach((key) => {
      if (key in source) {
        officeInfo[key] = key === 'productivity' ? normalizeProductivity(source[key]) : source[key];
      }
    });
    slice.office_info = officeInfo;
  }

  return slice;
};

/**
 * Overlay the org-service office slice (from `toOfficeSlice`) onto the monolith
 * `currentUser`. The paired second half of the transform above: `toOfficeSlice`
 * owns WHICH fields org-service carries and their wire reshape, so this only has
 * to combine two objects correctly.
 *
 * A faithful, non-mutating overlay. The one subtlety is `office_info`, which is
 * split across the two sources — org-service owns some subfields (officeType,
 * productivity, …) while the monolith keeps the T&C subfields
 * (gotuTerms/psaTerms) — so it is deep-merged rather than replaced. Every other
 * field is a top-level overlay.
 *
 * @template {object} T
 * @param {T} currentUser - the `/admin/users/:id` user (source of truth for
 *   everything org-service does not own)
 * @param {object} officeSlice - allowlisted office fields from `toOfficeSlice`
 * @returns {T} a new merged user; inputs are never mutated
 */
export const mergeOfficeSlice = (currentUser, officeSlice = {}) => {
  const merged = { ...currentUser, ...officeSlice };

  if (officeSlice.office_info || currentUser.office_info) {
    merged.office_info = { ...currentUser.office_info, ...officeSlice.office_info };
  }

  return merged;
};

/**
 * Office adapters, grouped to match the `UserAdapters` convention.
 */
export const OfficeAdapters = {
  normalizeProductivity,
  toOfficeSlice,
  mergeOfficeSlice,
};
